"use client"

import Link from "next/link"
import { ApertureStyles, T, FM, MAXW, SectionEyebrow, ArrowR } from "./_shared"
import { PlanBadge } from "@/components/ui/PlanBadge"

/**
 * The platform — the things you get *around* the eight validators.
 *
 * Sits between the Validators bento (what we DO to data) and the
 * Compare table (why we're 10x cheaper). Reads as: "and on top of
 * the validation pipeline, here's the rest of the toolkit."
 *
 * Six cards, each pointing at a real shipped feature. Copy follows
 * the Aperture-Light voice: concrete verbs, one promise per card,
 * no hand-wave words. Every link goes somewhere real (docs anchor,
 * dashboard route, or API ref).
 */

interface Feature {
  k: string                                  // trace tag (/01, /02…) — anchors the editorial voice
  id: string
  icon: React.ReactNode
  title: string
  body: string
  href: string                               // destination — must resolve
  cta: string                                // link text
  pro?: boolean                              // shows a prominent PRO chip + faint emerald wash on the card
}

const FEATURES: Feature[] = [
  {
    k: "01",
    id: "auto-repair",
    icon: <IconRepair />,
    title: "Auto-repair, not just flag",
    body: "Typo guard sees gmial.com → gmail.com, info@stripe.con → info@stripe.com. We don't drop them — we fix them, and you decide row-by-row what to keep.",
    href: "/docs#auto-repair",
    cta: "How it works",
  },
  {
    k: "02",
    id: "mapper",
    icon: <IconMapper />,
    title: "ESP-aware export mapper",
    body: "Mailchimp wants Email Address. HubSpot wants email. Klaviyo wants $email. Pick your destination, we rewrite the CSV columns to match.",
    href: "/docs#export-mapper",
    cta: "Supported ESPs",
    pro: true,
  },
  {
    k: "03",
    id: "compare-files",
    icon: <IconCompare />,
    title: "Cross-list compare",
    body: "Drop your suppression list. Drop your prospect list. We tell you what's new, what overlaps, and what to send — no spreadsheet acrobatics.",
    href: "/docs#compare",
    cta: "How it works",
  },
  {
    k: "04",
    id: "api",
    icon: <IconApi />,
    title: "Public API",
    body: "POST /api/v1/clean. Bearer auth. Same engine as the dashboard, programmatic. Fits in any pipeline that talks HTTP.",
    href: "/docs#api",
    cta: "API overview",
    pro: true,
  },
  {
    k: "05",
    id: "webhooks",
    icon: <IconWebhook />,
    title: "Webhooks",
    body: "job.completed and dataset.ready fire the moment your run finishes. No polling, no client-side timers — just hand us a URL.",
    href: "/docs#webhooks",
    cta: "Event reference",
    pro: true,
  },
  {
    k: "06",
    id: "audit-trail",
    icon: <IconAudit />,
    title: "Per-row audit trail",
    body: "Every rejection comes with the rule that flagged it. Hand the same CSV to compliance, your CMO, and the auditor — they all read the same column.",
    href: "/docs#audit-trail",
    cta: "What's recorded",
  },
]

export default function ApertureplatformSection() {
  return (
    <section id="platform" className="relative scroll-mt-24" style={{ background: T.bg }}>
      <ApertureStyles />
      <div className={`${MAXW} mx-auto px-6 lg:px-10 py-12 sm:py-16 md:py-24`}>

        {/* Heading — same eyebrow + headline pattern as the rest of
            the page so this section reads as part of the same magazine,
            not a different document. */}
        <div className="mb-14 max-w-[720px]">
          <SectionEyebrow>The platform</SectionEyebrow>
          <h2
            className="mt-3 leading-[1.0] tracking-[-0.035em]"
            style={{ fontSize: "clamp(2rem, 4vw, 3.1rem)", fontWeight: 800 }}
          >
            Nine checks are the engine.{" "}
            <em style={{ color: T.acc, fontStyle: "italic", fontWeight: 700 }}>
              Here&rsquo;s the rest of the dashboard.
            </em>
          </h2>
          <p className="mt-5" style={{ fontSize: 15.5, color: T.ink2, lineHeight: 1.6, maxWidth: "60ch" }}>
            Validation is half the job. The other half is the boring infrastructure
            around it — fixing typos so they don&rsquo;t bounce, mapping columns to your
            ESP, comparing lists, and automating it via API. One resolved dataset powers
            every action; plan limits are shown before you commit.
          </p>
        </div>

        {/* Bento grid — 3 columns at lg, 2 at md, 1 at sm. Cards have
            consistent surface (white over cream) and a mint hairline
            top stroke that lights up on hover. Pure type + iconography,
            no skeuomorphism, no glow nonsense. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <FeatureCard key={f.k} f={f} />
          ))}
        </div>
      </div>
    </section>
  )
}

function FeatureCard({ f }: { f: Feature }) {
  return (
    <Link
      id={f.id}
      href={f.href}
      className="group relative flex flex-col rounded-2xl overflow-hidden transition-all duration-200 hover:-translate-y-[1px]"
      style={{
        // Pro cards get a faint emerald wash + a stronger border so
        // they feel like a different "tier" without being garish.
        background: f.pro
          ? `linear-gradient(180deg, ${T.surface} 0%, rgba(15,81,50,0.025) 100%)`
          : T.surface,
        border: `1px solid ${f.pro ? "rgba(15,81,50,0.22)" : T.hair}`,
        boxShadow: f.pro
          ? `0 1px 0 rgba(255,255,255,0.65) inset, 0 0 0 0 rgba(15,81,50,0)`
          : `0 1px 0 rgba(255,255,255,0.65) inset`,
        padding: "1.4rem 1.4rem 1.2rem",
        minHeight: 220,
      }}
    >
      {/* Pro cards get a vivid mint hairline at the top; non-Pro
          cards get a subtle one. Same SVG slot, different opacity. */}
      <div
        aria-hidden
        className="absolute inset-x-4 top-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${T.acc} 50%, transparent)`,
          opacity: f.pro ? 0.85 : 0.35,
        }}
      />

      {/* Trace tag + icon row */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <span
          className="inline-flex items-center justify-center rounded-lg flex-shrink-0"
          style={{
            width: 36, height: 36,
            background: f.pro ? T.acc : T.accSoft,
            border: `1px solid ${f.pro ? T.acc : "rgba(15,81,50,0.18)"}`,
            color: f.pro ? "#FFFFFF" : T.acc,
            boxShadow: f.pro ? `0 6px 16px -8px rgba(15,81,50,0.45)` : undefined,
          }}
        >
          {f.icon}
        </span>
        <div className="flex items-center gap-2">
          {f.pro ? <PlanBadge plan="pro" /> : null}
          <span
            style={{
              fontFamily: FM,
              fontSize: 10.5,
              letterSpacing: "0.16em",
              color: T.ink3,
              fontWeight: 700,
            }}
          >
            /{f.k}
          </span>
        </div>
      </div>

      {/* Title + body */}
      <h3
        className="leading-[1.2] tracking-[-0.015em]"
        style={{ fontSize: 16.5, fontWeight: 700, color: T.ink }}
      >
        {f.title}
      </h3>
      <p className="mt-2 flex-1" style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.55 }}>
        {f.body}
      </p>

      {/* CTA row */}
      <span
        className="mt-4 inline-flex items-center gap-1.5 transition-colors group-hover:text-[#0F5132]"
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: f.pro ? T.acc : T.ink2,
          letterSpacing: "-0.005em",
        }}
      >
        {f.cta} <ArrowR size={11} />
      </span>
    </Link>
  )
}

/* ─── Inline icons (lucide-style 18×18, currentColor) ─────────────── */

function IconRepair() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  )
}

function IconMapper() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="9" y2="6" />
      <line x1="3" y1="12" x2="9" y2="12" />
      <line x1="3" y1="18" x2="9" y2="18" />
      <line x1="15" y1="6" x2="21" y2="6" />
      <line x1="15" y1="12" x2="21" y2="12" />
      <line x1="15" y1="18" x2="21" y2="18" />
      <path d="M9 9 L15 15" />
      <path d="M9 15 L15 9" />
    </svg>
  )
}

function IconCompare() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="12" r="6" />
      <circle cx="15" cy="12" r="6" />
    </svg>
  )
}

function IconApi() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  )
}

function IconWebhook() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" />
      <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" />
      <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" />
    </svg>
  )
}

function IconAudit() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="15" y2="17" />
      <line x1="9" y1="9" x2="11" y2="9" />
    </svg>
  )
}
